import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { ProductListItemDto } from '../../dtos/product-list-item.dto';
import { ProductService } from '../../pages/product-page/services/product.service';
import { SortingPaginatingFilterDto } from '../../dtos/spf.dto';
import { finalize } from 'rxjs/operators';
import { DeviceService } from '../../services/device-detector/device.service';
import { onWindowLoad } from '../../helpers/on-window-load.function';
import { WindowResizeService } from '../../services/window-resize.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProductListItemComponent } from '../product-list-item/product-list-item.component';
import { PreloaderComponent } from '../preloader/preloader.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'products-preview',
  templateUrl: './products-preview.component.html',
  styleUrls: ['./products-preview.component.scss'],
  imports: [ProductListItemComponent, PreloaderComponent, TranslatePipe],
})
export class ProductsPreviewComponent implements AfterViewInit {
  private productService = inject(ProductService);
  private cdr = inject(ChangeDetectorRef);
  private device = inject(DeviceService);
  private windowResizeService = inject(WindowResizeService);

  private itemWidth: number;
  private activeItemIdx: number = 0;

  private swipeCoord: [number, number];
  private swipeTime: number;

  isLoading = signal<boolean>(false);
  resolvedItems = signal<ProductListItemDto[]>([]);

  readonly itemsToShow = input<number>(undefined);
  readonly items = input<ProductListItemDto[]>([]);
  readonly ids = input<number[]>(undefined);
  readonly type = input<string>(undefined);
  readonly parentNameForAnalytics = input<string>(undefined);

  readonly visibleItemsCount = computed<number>(() => {
    const type = this.type();
    if (this.device.isMobile()) {
      return 2;
    }

    return type ? 5 : 4;
  });
  readonly isArrowVisible = computed<boolean>(() => this.resolvedItems().length > this.visibleItemsCount());

  readonly itemsContainerRef = viewChild<ElementRef>('itemsContainerRef');
  readonly itemRef = viewChildren<ElementRef>('itemRef');

  private readonly destroyRef = inject(DestroyRef);

  ngAfterViewInit(): void {
    if (this.ids()) {
      onWindowLoad(() => this.fetchItems());
    } else if (this.items()) {
      this.resolvedItems.set(this.items());
      this.handleResize();
    }
  }

  private fetchItems() {
    const ids = this.ids();
    if (!ids?.length || !this.device.isPlatformBrowser()) { return; }

    const spf = new SortingPaginatingFilterDto();
    spf.id = ids;

    this.isLoading.set(true);
    this.cdr.detectChanges();
    this.productService.fetchProductsByFilters(spf)
      .pipe( finalize(() => this.isLoading.set(false)) )
      .subscribe(
        response => {
          this.setItems(response.data);
          this.handleResize();
        }
      );
  }

  prev() {
    if (this.activeItemIdx === 0) {
      this.activeItemIdx = this.resolvedItems().length - this.visibleItemsCount();
    } else if (this.activeItemIdx < this.visibleItemsCount()) {
      this.activeItemIdx = 0;
    } else {
      this.activeItemIdx -= this.visibleItemsCount();
    }

    this.setOffset();
  }

  next() {
    const nextIdx = this.activeItemIdx + this.visibleItemsCount();

    if (nextIdx > this.resolvedItems().length - 1) {
      this.activeItemIdx = 0;
    } else if (nextIdx > this.resolvedItems().length - this.visibleItemsCount()) {
      this.activeItemIdx = this.resolvedItems().length - this.visibleItemsCount();
    } else {
      this.activeItemIdx = nextIdx;
    }

    this.setOffset();
  }

  private setItems(items: ProductListItemDto[]) {
    this.resolvedItems.set(items.sort((a, b) => {
      const indexOfA = this.ids().indexOf(a.id);
      const indexOfB = this.ids().indexOf(b.id);

      if (indexOfA > indexOfB) {
        return 1;
      } else if (indexOfA < indexOfB) {
        return -1;
      } else {
        return 0;
      }
    }));
  }

  private handleResize() {
    if (this.device.isPlatformServer()) { return; }

    this.cdr.detectChanges();
    this.setSizes();

    this.windowResizeService.resized$
      .pipe( takeUntilDestroyed(this.destroyRef) )
      .subscribe(() => this.setSizes())
  }

  private setSizes() {
    const containerWidth = this.itemsContainerRef().nativeElement.parentElement.clientWidth;

    this.itemWidth = containerWidth / this.visibleItemsCount();

    this.itemRef().forEach(itemRef => itemRef.nativeElement.style.width = `${this.itemWidth}px`);
  }

  private setOffset() {
    const x = this.activeItemIdx * this.itemWidth;
    this.itemsContainerRef().nativeElement.style.transform = `translate3d(-${x}px, 0, 0)`;
    this.cdr.detectChanges();
  }

  public swipe(e: TouchEvent, action: string): void {
    const coord: [number, number] = [e.changedTouches[0].clientX, e.changedTouches[0].clientY];
    const time = new Date().getTime();

    if (action === 'start') {
      this.swipeCoord = coord;
      this.swipeTime = time;
    } else if (action === 'end') {
      const direction = [coord[0] - this.swipeCoord[0], coord[1] - this.swipeCoord[1]];
      const duration = time - this.swipeTime;

      if (duration < 1000
        && Math.abs(direction[0]) > 30
        && Math.abs(direction[0]) > Math.abs(direction[1] * 3)
      ) {
        const swipe = direction[0] < 0 ? 'next' : 'previous';

        if (swipe === 'next') {
          this.next();
        } else {
          this.prev();
        }
      }
    }
  }
}
